import { useEffect, useState } from 'react';
import {
    Session,
    SessionOnConnectionBrokenParams,
    SessionOnConnectionEstablishedParams,
    SessionOnEndedParams,
    SessionOnMessageParams,
    SessionStatus,
    SessionStatusHandler,
} from '../sessions/Session';
import * as api from '@amzn/ring-cs-chatbot-service-model';
import {
    ContentTypeEnumSchema,
    JsonContentTypeEnumSchema,
} from '@amzn/ring-cs-chatbot-service-model';
import { ReceiptData, ReceiptManager } from './ReceiptManager';
import { TypingIndicatorManager } from './TypingIndicatorManager';
import { PendingMessage, PendingMessageManager } from './PendingMessageManager';
import { marked } from 'marked';
import { generateHTML } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import {
    MultiSelectNode,
    OptionNode,
    ProductNode,
    RatingNode,
    SelectNode
} from '../../utils/ChatBotExtensions';

// Configurable list of message types that should have their response content processed as markdown
const MARKDOWN_RESPONSE_TYPES: string[] = [
    JsonContentTypeEnumSchema.enum['application/chat.message.sherpa.response'],
    JsonContentTypeEnumSchema.enum['application/chat.message.sherlock.response']
];

export type EnrichedMessage = api.ResponseMessage & {
    jsonContent?: api.JsonContent;
};

/**
 * Messages will be added, but never removed from the data structure below.
 * Messages must always be retrieved in sorted order and immediately rendered
 * by React, so the returned reference should be unique. Additionally, messages
 * will only be added from the beginning (transcripting) or the end (new
 * messages). This lends itself generally to a sorted linked list where
 * messages are added to the head or tail. The end to add will be determined by
 * checking which end has a closer absolute time to the message being added.
 * Finally, since messages could be added slightly out of order, an additional
 * step will be performed after add to shift it into place if slightly out of
 * order.
 *
 * Time complexity: add O(1) retrieveSorted O(n)
 * Space complexity: O(n)
 */
type Node<T> = {
    prev?: Node<T>;
    next?: Node<T>;
    element: T;
};

class SortedMessagesLinkedList {

    private messageIds: Set<string>;
    private head?: Node<EnrichedMessage>;
    private tail?: Node<EnrichedMessage>;

    constructor () {
        this.messageIds = new Set();
        this.head = undefined;
        this.tail = undefined;
    }

    [Symbol.iterator] () {
        let node = this.head;

        return {
            next: () => {
                let response: IteratorResult<EnrichedMessage>;
                if (node) {
                    response = {
                        done: false,
                        value: node.element,
                    };
                    node = node.next;
                } else {
                    response = {
                        done: true,
                        value: undefined,
                    };
                }
                return response;
            },
        };
    }

    add = (m: EnrichedMessage) => {
        // do not add messages already in the list
        if (this.messageIds.has(m.messageId)) {
            return false;
        }

        const node: Node<EnrichedMessage> = {
            prev: undefined,
            next: undefined,
            element: m,
        };
        if (!this.head || !this.tail) {
            this.head = this.tail = node;
            this.messageIds.add(node.element.messageId);
            return true;
        }

        const headDiff = Math.abs(this.head.element.absoluteTime - m.absoluteTime);
        const tailDiff = Math.abs(this.tail.element.absoluteTime - m.absoluteTime);

        if (headDiff <= tailDiff) {
            // add at the head
            node.next = this.head;
            this.head = node;
            this.head.next!.prev = this.head;
        } else {
            // add at the tail
            node.prev = this.tail;
            this.tail = node;
            this.tail.prev!.next = this.tail;
        }

        this.checkSwap(node);
        this.messageIds.add(node.element.messageId);
        return true;
    };

    private checkSwap = (node: Node<EnrichedMessage>) => {
        while (true) {
            if (node.prev && node.prev.element.absoluteTime > node.element.absoluteTime) {
                this.swapConsecutiveNodes(node.prev, node);
                continue;
            }

            if (node.next && node.next.element.absoluteTime < node.element.absoluteTime) {
                this.swapConsecutiveNodes(node, node.next);
                continue;
            }

            break;
        }
    };

    private swapConsecutiveNodes = (n1: Node<EnrichedMessage>, n2: Node<EnrichedMessage>) => {
        console.info('swapping');
        const prev = n1.prev;
        const next = n2.next;

        // link prev <-> n2
        n2.prev = prev;
        if (prev) {
            prev.next = n2;
        } else {
            this.head = n2;
        }

        // link n2 <-> n1
        n2.next = n1;
        n1.prev = n2;

        // link n1 <-> next
        n1.next = next;
        if (next) {
            next.prev = n1;
        } else {
            this.tail = n1;
        }
    };
}

export type MessagesUpdateHandler = (messages: EnrichedMessage[]) => void;
export type OnEventHandler = (messages: api.ResponseMessage) => void;

export class ManagedSession implements Session {
    private readonly wrappedSession: Session;
    private readonly typingIndicatorManager: TypingIndicatorManager;
    private readonly receiptManager: ReceiptManager;
    private readonly pendingMessageManager: PendingMessageManager;

    private readonly messagesOrderedList: SortedMessagesLinkedList;
    private messages: EnrichedMessage[]; // memo messages array from ordered list

    private readonly messagesUpdateHandlers: Set<MessagesUpdateHandler> = new Set();

    private readonly onEventHandlers: Set<OnEventHandler> = new Set();

    private customOnMessageHandlers: Set<(message: api.ResponseMessage) => void> = new Set();

    private readonly tipTapExtensions = [
        StarterKit,
        Link,
        MultiSelectNode,
        OptionNode,
        ProductNode,
        RatingNode,
        SelectNode
    ];

    constructor (session: Session) {
        this.wrappedSession = session;
        this.typingIndicatorManager = new TypingIndicatorManager(this);
        this.receiptManager = new ReceiptManager(this);
        this.pendingMessageManager = new PendingMessageManager(this);

        // initialize ordered list and memo it
        this.messagesOrderedList = new SortedMessagesLinkedList();
        this.messages = [...this.messagesOrderedList];

        this.wrappedSession.onMessage({ handler: this.onMessageHandler });
    }

    connect = () => this.wrappedSession.connect();

    getAttributes = (): Record<string, string> => {
        return this.wrappedSession.getAttributes();
    };

    getSessionId = (): string | undefined => {
        return this.wrappedSession.getSessionId();
    };


    sendMessage = async (params: api.SendMessageRequest) => {
        const response = await this.wrappedSession.sendMessage(params);

        if (response.message) {
            this.onMessageHandler(response.message);
        }

        return response;
    };

    transcript = async (params: api.TranscriptRequest) => {
        const response = await this.wrappedSession.transcript(params);

        this.processMessages(...response.transcript);
        this.receiptManager.hydrateReceipts(response.transcript);

        return response;
    };

    end = async () => {
        await this.wrappedSession.end();
    }

    clear = () => {
        this.wrappedSession.clear();
    }

    onStatusUpdate = (handler: SessionStatusHandler) =>
        this.wrappedSession.onStatusUpdate(handler);

    onConnectionEstablished = (params: SessionOnConnectionEstablishedParams) =>
        this.wrappedSession.onConnectionEstablished(params);

    onConnectionBroken = (params: SessionOnConnectionBrokenParams) =>
        this.wrappedSession.onConnectionBroken(params);

    onMessage = (params: SessionOnMessageParams) => {
        this.customOnMessageHandlers.add(params.handler);

        return () => {
            this.customOnMessageHandlers.delete(params.handler);
        };
    };

    onEnded = (params: SessionOnEndedParams) =>
        this.wrappedSession.onEnded(params);

    private onMessageHandler = (apiMessage: api.ResponseMessage) => {
        const message: EnrichedMessage = { ...apiMessage };

        let sendToHandlers: boolean;
        switch (message.contentType) {
            case (ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.joined']):
            case (ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.left']):
            case (ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.idle']):
            case (ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.returned']):
            case (ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.chat.ended']):
            case (ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.transfer.succeeded']):
            case (ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.transfer.failed']):
                // these are persisted
                sendToHandlers = this.processMessages(message);
            case (ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.typing']):
                // send events to handlers
                this.onEventHandlers.forEach(handler => handler(message));
                sendToHandlers = true;
                break;
            case (ContentTypeEnumSchema.enum['application/json']):
            case (ContentTypeEnumSchema.enum['text/markdown']):
            case (ContentTypeEnumSchema.enum['text/plain']):
                sendToHandlers = this.processMessages(message);
                break;
            default:
                if (message.type == api.MessageType.MESSAGE) {
                    sendToHandlers = this.processMessages(message);
                } else if (message.type == api.MessageType.EVENT) {
                    this.onEventHandlers.forEach(handler => handler(message));
                    sendToHandlers = true;
                } else {
                    sendToHandlers = false;
                }
                break;
        }

        if (sendToHandlers) {
            this.customOnMessageHandlers.forEach(handler => handler(message));
        }
    };

    private enrichJsonMessage = (message: EnrichedMessage) => {
        if (message.contentType === ContentTypeEnumSchema.enum['application/json'] && message.content) {
            try {
                const parsed = JSON.parse(message.content);
                message.jsonContent = parsed;

                // Process RichText JSON messages
                if (parsed.type === JsonContentTypeEnumSchema.enum['application/chat.json.v1'] && parsed.doc) {
                    try {
                        const htmlContent = generateHTML(parsed.doc, this.tipTapExtensions);
                        message.content = htmlContent;
                        message.contentType = ContentTypeEnumSchema.enum['text/plain'];
                    } catch (error) {
                        console.warn('Failed to process TipTap JSON message:', error);
                        // Keep original content on error for fallback handling
                    }
                } else if (MARKDOWN_RESPONSE_TYPES.includes(parsed.type) && message.jsonContent?.response) {
                    // marked.parse will add a new line at the end of the string, so we need to trim it
                    const plainHtmlContent = marked.parse(message.jsonContent.response, {
                        async: false,
                    }).trim();

                    message.jsonContent.response = plainHtmlContent;
                }
            } catch (err) {
                console.warn('Unable to parse application/json message');
            }
        }
    };

    private enrichMarkdownMessage = (message: EnrichedMessage) => {
        if (message.contentType === ContentTypeEnumSchema.enum['text/markdown'] && message.content) {

            // marked.parse will add a new line at the end of the string, so we need to trim it
            const plainHtmlContent = marked.parse(message.content, {
                async: false,
            }).trim();

            message.content = plainHtmlContent;
            message.contentType = ContentTypeEnumSchema.enum['text/plain'];
        }
    };

    /**
     * Enriches and processes incoming messages into linked list data structure.
     * Centralize our JSON enrichment logic so that both real‑time and transcript
     * messages are handled uniformly.
     *
     * Returns true if any messages were processed, false otherwise.
     */
    private processMessages = (...messages: api.ResponseMessage[]) => {
        let messagesProcessed = false;
        for (const message of messages) {
            switch (message.contentType) {
                case (ContentTypeEnumSchema.enum['application/json']):
                    this.enrichJsonMessage(message as EnrichedMessage);
                    break;
                case (ContentTypeEnumSchema.enum['text/markdown']):
                    this.enrichMarkdownMessage(message as EnrichedMessage);
                    break;
            }

            messagesProcessed = this.messagesOrderedList.add(message) || messagesProcessed; // O(1)
        }

        if (messagesProcessed) {
            this.messages = [...this.messagesOrderedList]; // O(n)
            this.messagesUpdateHandlers.forEach(handler => handler(this.messages));
            return true;
        }

        return false;
    };

    onMessagesUpdate = (handler: MessagesUpdateHandler) => {
        this.messagesUpdateHandlers.add(handler);

        handler(this.messages);

        return () => {
            this.messagesUpdateHandlers.delete(handler);
        };
    };

    onEvent = (handler: OnEventHandler) => {
        this.onEventHandlers.add(handler);

        return () => {
            this.onEventHandlers.delete(handler);
        };
    };

    triggerTyping = () => {
        this.sendMessage({
            message: {
                type: api.MessageType.EVENT,
                contentType: api.SendEventContentTypeSchema.enum['application/vnd.amazonaws.connect.event.typing'],
            },
        });
    };

    useSessionStatus = () => {
        const [status, setStatus] = useState<SessionStatus>(SessionStatus.CREATED);

        useEffect(() => {
            return this.onStatusUpdate((status) => {
                setStatus(status);
            });
        }, [this]);

        return status;
    };

    useMessages = () => {
        const [messages, setMessages] = useState<EnrichedMessage[]>(this.messages);

        useEffect(() => {
            return this.onMessagesUpdate((messages) => {
                setMessages(messages);
            });
        }, [this]);

        return messages;
    };

    useTyping = (): api.MessageParticipantRole | undefined => {
        const [typingRole, setTypingRole] = useState<api.MessageParticipantRole | undefined>(undefined);

        useEffect(() => {
            return this.typingIndicatorManager.onTypingChange(setTypingRole);
        }, [this]);

        return typingRole;
    };

    usePendingMessage = (): PendingMessage | undefined => {
        const [pendingMessage, setPendingMessage] = useState<PendingMessage>();

        useEffect(() => {
            return this.pendingMessageManager.onPendingChange(setPendingMessage);
        }, [this]);

        return pendingMessage;
    };


    useReceipts = (reading: boolean): ReceiptData => {
        const [receiptData, setReceiptData] = useState(this.receiptManager.receiptContainer.value);

        useEffect(() => {
            return this.receiptManager.receiptContainer.onUpdate((receiptData) => {
                setReceiptData(receiptData);
            });
        }, [this]);

        useEffect(() => {
            this.receiptManager.updateReading(reading);
        }, [this, reading]);

        return receiptData;
    };

}
